import React, { useEffect, useState } from 'react'
import kpiStyles from './KPI.module.scss'
import chroma from 'chroma-js'
import {
    getCompareDiff,
    getCompareDirection,
    getFormattedKPIValue,
    getKPIData,
    getKPIStyles,
    getKPITitle,
} from './utils'
import { ScoopSpinner } from '../../ui'
import { CompareArrow } from './CompareArrow'
import { useSelector } from 'react-redux'

export const KPI = ({ config, setConfig, server, prompts, embeddedSizeProps, theme, chat }) => {
    const [loading, setLoading] = useState(false)
    const [kpiValue, setKpiValue] = useState(0)
    const [compareDirection, setCompareDirection] = useState('')
    const [compareValue, setCompareValue] = useState(0)
    const [compareDiff, setCompareDiff] = useState(0)
    const [metricFormat, setMetricFormat] = useState('')
    const styles = getKPIStyles(theme, config, embeddedSizeProps, kpiValue)
    const lastEditedPrompt = useSelector((state) => state.prompt.lastEditedPrompt)
    const compareColor =
        compareDirection === 'up'
            ? styles.compareStyles.positiveColor
            : styles.compareStyles.negativeColor

    useEffect(() => {
        if (config.selectedItems.length > 0) {
            setLoading(true)
            getKPIData(config, server, prompts, (r) => {
                if (config.tableTimeSeries) {
                    const data = r.series[0]?.data
                    if (data) {
                        const lastValue = data[data.length - 1]
                        const secondToLastValue = data[data.length - 2]
                        setMetricFormat(r.metrics[0].format || '')
                        setKpiValue(lastValue)
                        setCompareValue(secondToLastValue)
                        setCompareDiff(
                            getCompareDiff(lastValue, secondToLastValue, config.kpiCompareType)
                        )
                        setCompareDirection(getCompareDirection(lastValue, secondToLastValue))
                    } else {
                        setMetricFormat('')
                        setKpiValue(0)
                        setCompareValue(0)
                        setCompareDiff(0)
                        setCompareDirection('')
                    }
                } else {
                    const data = r.dataset?.series[0]?.data
                    if (data) {
                        const lastValue = data[0]
                        setMetricFormat(r.dataset.metrics[0].format || '')
                        setKpiValue(lastValue)
                        const newConfig = { ...config }
                        newConfig.kpiCompareTarget = lastValue
                        setConfig(newConfig)
                    } else {
                        setMetricFormat('')
                        setKpiValue(0)
                    }
                }
                setLoading(false)
            })
        }
    }, [
        config.period,
        config.calendarType,
        config.timeRange,
        config.tableTimeSeries,
        lastEditedPrompt,
    ])

    useEffect(() => {
        if (config.kpiCompareTarget && !config.tableTimeSeries) {
            setCompareValue(config.kpiCompareTarget)
            setCompareDiff(getCompareDiff(kpiValue, config.kpiCompareTarget, config.kpiCompareType))
            setCompareDirection(getCompareDirection(kpiValue, config.kpiCompareTarget))
        } else {
            setCompareDiff(getCompareDiff(kpiValue, compareValue, config.kpiCompareType))
        }
    }, [config.kpiCompareTarget, config.tableTimeSeries, kpiValue, config.kpiCompareType])

    return (
        <div
            className={kpiStyles.container}
            style={{
                ...styles.containerStyles,
                justifyContent: config.kpiCompareType !== 'none' ? 'space-between' : 'center',
                width: embeddedSizeProps?.width || (chat ? 250 : 200),
                height: embeddedSizeProps?.height || 200,
                margin: embeddedSizeProps ? '' : 'auto',
            }}
        >
            {loading ? (
                <div className={kpiStyles.loading}>
                    <ScoopSpinner size={60} />
                </div>
            ) : (
                <>
                    <div className={kpiStyles.title} style={styles.titleStyles}>
                        {getKPITitle(config)}
                    </div>
                    <div style={styles.valueStyles}>
                        {kpiValue && getFormattedKPIValue(kpiValue, metricFormat)}
                    </div>
                    {config.kpiCompareType !== 'none' && (
                        <div className={kpiStyles.compare}>
                            {compareDirection !== 'equal' && (
                                <div
                                    className={kpiStyles.compareIcon}
                                    style={{
                                        backgroundColor: chroma(compareColor).alpha(0.2).hex(),
                                    }}
                                >
                                    <CompareArrow
                                        width={styles.compareStyles.fontSize}
                                        height={styles.compareStyles.fontSize}
                                        direction={compareDirection}
                                        fill={compareColor}
                                    />
                                    <span
                                        style={{
                                            ...styles.compareStyles,
                                            color: compareColor,
                                        }}
                                    >
                                        {compareDiff === 0 ? '-' : compareDiff}
                                    </span>
                                </div>
                            )}
                            <span style={{ ...styles.compareStyles }}>
                                {'(' + getFormattedKPIValue(compareValue, metricFormat) + ')'}
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
